import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import _ from 'underscore';
import moment from 'moment';

import * as actions from './actions';
import { verifyAuthState } from 'global/actions/authActions';
import services from 'api/services';
import { useQueryParams } from 'hooks/useQueryParams';
import { DistributionChart } from 'user/components/DistributionChart/distributionChart';
import CategoryDistribution from './components/categoryGraph';
import { useActions } from 'hooks/useActions';
import { Images } from 'util/images';
import { calculatePercentage, getReportCalculationData } from 'functions/testResult';
import { Spinner } from 'components/Spinner/spinner';
import InformationModal from 'components/Modals/informationModal';
import TabSelector from 'user/components/TabSelector/tabSelector';
import { Wrapper, Container, Button } from 'user/components';
import MCQAnswersTab from './components/mcqAnswersTab';
import CodeTab from './components/codeTab';
import InfoBox from './components/infoBox';
import TestCases from './components/testCases';
import { modalInformation } from './data';
import { ChangeDepartmentDropdown, DownloadReportDropdown } from './components/buttonDropdown';
import YesNoModal from 'components/Modals/yesNoModal';
import { getConfig } from 'functions/getConfig';
import { categories, userRoles } from 'util/appConstants';
import {
  InfoAndScoreContainer,
  InfoAndScoreBox,
  Heading,
  HeadingUnderLine,
  LabelHeading,
  LabelValue,
  GaugeImage,
  PointerImage,
  ScoreDescriptionContainer,
  PercentageText,
  InfoIcon,
  InfoIconContainer,
  ScoreBoxContainer,
  GaugeAndPointerContainer,
  ActiveTabBox,
  DescriptionHeading,
  Description,
  FreecodeScoreContainer,
  FreecodeScore,
  SeparateContainer,
  SeparateInnerContainer,
  FlexContainer,
  CapsuledTab,
  LeftCapsuledTab,
  RightCapsuledTab,
  QuestionNumberContainer,
  NoQuestionText,
  ButtonContainer,
  FutureSkillLogo,
  InnerBorderPrintContainer,
  BorderPrintContainer,
  ReportToast,
  ReportTextContainer,
  ReportTitle,
  ReportInfo,
} from './styles';

const { TicketService } = services;

const ReportPage = () => {
  let { id, session } = useParams();
  const { version, anonymiseReport: anonymiseReportParam, showDescription: showDescriptionParam } = useQueryParams();

  let anonymiseReport = false;
  let showDescription = false;
  if (showDescriptionParam) showDescription = showDescriptionParam === 'false' ? false : true;
  if (anonymiseReportParam) anonymiseReport = anonymiseReportParam === 'false' ? false : true;
  const isCompleteReport = version === 'complete';

  const { loadReport, updateTicketDepartment } = useActions(actions);
  let { loading, error, report, updatedTicket } = useSelector((state) => state.report);
  let { user, userRole } = useSelector((state) => state.auth);

  const [isFreecode, setIsFreecode] = useState(false);
  const [showScoreInstructionsModal, setShowScoreInstructionsModal] = useState(false);
  const [bestRunInput, setBestRunInput] = useState({});
  const [timeUsed, setTimeUsed] = useState(null);
  const [completedAt, setCompletedAt] = useState(null);
  const [candidatePercentage, setCandidatePercentage] = useState(0);
  const [candidateRatingGauge, setCandidateRatingGauge] = useState(0);
  const [activeIndexTab, setActiveIndexTab] = useState(0);
  const [modalInformationKey, setModalInformationKey] = useState('totalStatistics');
  const [showFullPopulation, setShowFullPopulation] = useState(true);
  const [totalScore, setTotalScore] = useState(0);
  const [totalMaxScore, setTotalMaxScore] = useState(0);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [modalInfo, setModalInfo] = useState({});
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);

  // To avoid extra blank page in the candidate version of the report.
  // https://github.com/puppeteer/puppeteer/issues/589
  if (version === 'candidate') {
    let a = document.getElementById('root');
    a.style.overflow = 'hidden';
    a.style.marginBottom = '-1px';
  }

  useEffect(() => {
    if (session) {
      document.cookie = `fsSession=${session}; Domain=${getConfig().COOKIE}; Max-Age=50; Path=/; SameSite=Lax`;
      verifyAuthState();
    }
  }, [session]);

  useEffect(() => {
    if (user) {
      loadReport(id);
    }
  }, [user, loadReport, id]);

  useEffect(() => {
    if (error && !error.includes('internal error')) toast.error(error);
  }, [error]);

  useEffect(() => {
    if (updatedTicket._id) toast.success('Department changed successfully');
  }, [updatedTicket]);

  const downloadPdfReport = (type, anonymise, includeDescription) => {
    if (!id) return toast.error('Report id missing');
    if (isPdfDownloading) return toast.error('Already Downloading');
    setIsPdfDownloading(true);

    const doActualDownload = async () => {
      let link;
      try {
        toast.success(pdfDownloadPendingToast);
        const downloadPdfData = await TicketService.downloadPdfReport(id, type, anonymise, includeDescription);

        let fileName = ((report.ticket.name ?? report.candidate.name ?? 'Candidate') + '-' + report.test.name)
          .replace(/\s+/g, '-') // If space replace with dash
          .replace(/[^\w\-.]/g, ''); // If not word, number, dash, or dot, then remove
        if (anonymise) fileName = 'User';
        if (type === 'complete') fileName += '_Complete_Report';
        else fileName += '_Candidate_Report';

        link = document.createElement('a');
        link.href = `data:application/pdf;base64,${downloadPdfData}`;
        link.download = `${fileName}.pdf`;
        link.click();
      } catch (e) {
        toast.error(typeof e === 'string' ? e : e?.message || 'Error Downloading report');
      } finally {
        link?.remove?.();
        setIsPdfDownloading(false);
      }
    };
    doActualDownload();
  };

  const pdfDownloadPendingToast = () => (
    <ReportToast>
      <i className="fa fa-spinner fa-spin"></i>
      <ReportTextContainer>
        <ReportTitle>Generating PDF report</ReportTitle>
        <ReportInfo>Download starts in a few seconds</ReportInfo>
      </ReportTextContainer>
    </ReportToast>
  );

  useEffect(() => {
    if (!report.ticket) return;
    setIsFreecode(report.test.category === categories.CODE.category);

    let percentage = calculatePercentage(report.test, report.ticket, false);
    setCandidatePercentage(Math.round(percentage));
    setCandidateRatingGauge(Math.round(percentage) * (180 / 100));

    let finalSubmit = _.findWhere(report.answers, { isFinalSubmit: true });
    let bestRun = report?.ticket?.bestRun ? _.findWhere(report.answers, { isBestRun: true }) : finalSubmit;
    setBestRunInput(bestRun);

    let completionDate = finalSubmit
      ? finalSubmit.updatedAt
      : report.ticket.completedAt ||
        report.ticket.timeCompleted ||
        report.ticket.deadlineAt ||
        (report.ticket.startedAt ? moment(moment(report.ticket.startedAt) + 60000 * report.test.duration) : false);
    setCompletedAt(completionDate);
    let totalTimeUsed = report.ticket.deadlineAt
      ? Math.round(moment(completionDate).diff(moment(report.ticket.startedAt), 'minutes', true))
      : report.test.duration;
    setTimeUsed(totalTimeUsed);

    if (bestRun?.goodPerformanceScores?.length && report.test.category === 'code') {
      let totalLeveLScores = _.reduce(
        bestRun.levelScores,
        function (memo, num) {
          return memo + (num ? num : 0);
        },
        0
      );
      setTotalScore(totalLeveLScores);

      let totalLevelMaxScore = _.reduce(
        bestRun.levelMaxScores,
        function (memo, num) {
          return memo + (num ? num : 0);
        },
        0
      );
      setTotalMaxScore(totalLevelMaxScore);
    } else if ([categories.MULTICHOICE.category, categories.GENERAL_SKILLS.category].includes(report.test.category)) {
      setTotalMaxScore(report.ticket.totalQuestions ? report.ticket.totalQuestions : report.test.scale);
    }
  }, [report]);

  const getOvertimeUsed = () => {
    var difference = new Date(report.ticket.extendedDeadline) - new Date(report.ticket.startedAt);
    return Math.round(difference / 1000 / 60);
  };

  const renderScoreBox = () => {
    return (
      <ScoreBoxContainer>
        <GaugeAndPointerContainer>
          <GaugeImage src={Images.gaugeModal} />
          <PointerImage angle={candidateRatingGauge} />
        </GaugeAndPointerContainer>
        <ScoreDescriptionContainer>
          {report.test.adaptiveTest && !isFreecode && (
            <>
              <PercentageText>{candidatePercentage}%</PercentageText>
              <LabelValue>Better than {candidatePercentage}% of the reported results</LabelValue>
            </>
          )}
          {!report.test.adaptiveTest && !isFreecode && (
            <>
              <LabelHeading>Score:</LabelHeading>
              <LabelValue>
                &nbsp;{report.ticket.result}/{totalMaxScore}
              </LabelValue>
              <br />
              <LabelValue>This corresponds to {candidatePercentage}% correct answers</LabelValue>
            </>
          )}
          {isFreecode && (
            <>
              <PercentageText>{candidatePercentage}%</PercentageText>
              <LabelValue>
                The candidate scored {report.ticket.result} / {totalMaxScore}
              </LabelValue>
              <br />
              <LabelValue>which is better than {candidatePercentage}% of all the reported results</LabelValue>
            </>
          )}
        </ScoreDescriptionContainer>
        {(isFreecode || report.test.adaptiveTest) && (
          <InfoIconContainer>
            <InfoIcon
              icon="fa fa-info-circle"
              onClick={() => {
                setModalInformationKey('totalStatistics');
                setShowScoreInstructionsModal(true);
              }}
            />
          </InfoIconContainer>
        )}
      </ScoreBoxContainer>
    );
  };

  const renderDescriptionBox = () => {
    return (
      <ActiveTabBox forPrint={true}>
        <BorderPrintContainer>
          <InnerBorderPrintContainer>
            <Heading>TASK DESCRIPTION</Heading>
            <HeadingUnderLine />
            <DescriptionHeading>About {report.test.name}</DescriptionHeading>
            <Description dangerouslySetInnerHTML={{ __html: report.test.description }}></Description>
            {isFreecode && (
              <>
                <DescriptionHeading>Candidate Description</DescriptionHeading>
                <Description dangerouslySetInnerHTML={{ __html: report.test.candidateDescription }}></Description>
              </>
            )}
            {report.test.adaptiveTest && (
              <>
                <DescriptionHeading>About Adaptive Tests</DescriptionHeading>
                <Description dangerouslySetInnerHTML={{ __html: report.config.value }}></Description>
              </>
            )}
          </InnerBorderPrintContainer>
        </BorderPrintContainer>
      </ActiveTabBox>
    );
  };

  const renderStatistics = () => {
    if (isFreecode) {
      return (
        <ActiveTabBox>
          <Heading>BEST SCORE</Heading>
          <HeadingUnderLine />
          <FreecodeScoreContainer>
            <LabelHeading>{anonymiseReport ? 'Candidate' : report.ticket.name || report.candidate.name}</LabelHeading>
            <LabelValue>&nbsp;got</LabelValue>
            <FreecodeScore>
              {report.ticket.result} / {report.ticket.totalQuestions ? report.ticket.totalQuestions : report.test.scale}
            </FreecodeScore>
            <LabelValue>in score on this assignment</LabelValue>
            <InfoIconContainer>
              <InfoIcon
                icon="fa fa-info-circle"
                onClick={() => {
                  setModalInformationKey('candidateScore');
                  setShowScoreInstructionsModal(true);
                }}
              />
            </InfoIconContainer>
          </FreecodeScoreContainer>
        </ActiveTabBox>
      );
    } else {
      return renderDistribution(false);
    }
  };

  const renderDistribution = (extraBoxWithShadow) => {
    let reportData = getReportCalculationData(report.test, report.ticket, false);
    return (
      <SeparateContainer
        extraBoxWithShadow={extraBoxWithShadow}
        maxHeightForPrint={!extraBoxWithShadow ? '480px' : false}
      >
        <SeparateInnerContainer extraBoxWithShadow={extraBoxWithShadow}>
          <ActiveTabBox>
            <FlexContainer handleSmallDevice>
              <Heading>DISTRIBUTION</Heading>
              {reportData.hasDistribution && (
                <CapsuledTab>
                  <LeftCapsuledTab active={showFullPopulation} onClick={() => setShowFullPopulation(true)}>
                    Full population
                  </LeftCapsuledTab>
                  <RightCapsuledTab active={!showFullPopulation} onClick={() => setShowFullPopulation(false)}>
                    {report.company.name}'s population
                  </RightCapsuledTab>
                </CapsuledTab>
              )}
            </FlexContainer>
            <HeadingUnderLine />
            <DistributionChart
              test={report.test}
              isFreeCode={report.test.category === categories.CODE.category}
              showTotalTestsTaken={true}
              showCompanyPopulationDistribution={!showFullPopulation}
              ticket={report.ticket}
            />
            <InfoIconContainer>
              <InfoIcon
                icon="fa fa-info-circle"
                marginLeft="50"
                onClick={() => {
                  setModalInformationKey('totalDistribution');
                  setShowScoreInstructionsModal(true);
                }}
              />
            </InfoIconContainer>
          </ActiveTabBox>
        </SeparateInnerContainer>
      </SeparateContainer>
    );
  };

  const scrollToQuestion = (index) => {
    let element = document.getElementById(`question${index}`);
    let headerOffset = 60;
    let elementPosition = element.getBoundingClientRect().top;
    let offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  const renderQuestionsBox = () => {
    return report.questions.map((question, index) => (
      <QuestionNumberContainer key={question._id} correct={question.correct} onClick={() => scrollToQuestion(index)}>
        {index + 1}
      </QuestionNumberContainer>
    ));
  };

  const renderAnswersBox = () => {
    if (isFreecode)
      return (
        <CodeTab
          codeInputs={[...report.answers].sort((i1, i2) => new Date(i1.timeOfRequest) - new Date(i2.timeOfRequest))}
          ticket={report.ticket}
          bestRunInput={bestRunInput}
        />
      );
    return (
      <ActiveTabBox forPrint={true}>
        {report.answers.length ? (
          <BorderPrintContainer>
            <InnerBorderPrintContainer>
              <Heading>ANSWERS</Heading>
              <HeadingUnderLine />
              {renderQuestionsBox()}{' '}
            </InnerBorderPrintContainer>
          </BorderPrintContainer>
        ) : (
          <NoQuestionText>No questions answered</NoQuestionText>
        )}
      </ActiveTabBox>
    );
  };

  return (
    <Wrapper padding="60px 0 0 0" minHeight="100vh">
      <Container column>
        {loading && <Spinner />}
        {!loading && (
          <>
            {showConfirmationModal && (
              <YesNoModal
                modalInfo={modalInfo}
                confirm={(isConfirmed) => {
                  setShowConfirmationModal(false);
                  if (isConfirmed) updateTicketDepartment(report.ticket._id, modalInfo.department);
                }}
              />
            )}
            {showScoreInstructionsModal && modalInformationKey && (
              <InformationModal
                title={modalInformation[modalInformationKey]?.title}
                textHTML={modalInformation[modalInformationKey]?.textHTML}
                closeModal={() => setShowScoreInstructionsModal(false)}
              />
            )}
            {session && <FutureSkillLogo id="isDownloadable" src={Images.logoBlue} />}
            <ButtonContainer>
              <Button
                small
                primaryBlue
                primaryGreenBorder
                margin="5px"
                onClick={() => (window.location.href = `${getConfig().REDIRECTION_URL}/tests`)}
              >
                <i className="fa fa-arrow-left"></i>&nbsp;Back
              </Button>
              <DownloadReportDropdown
                isFreecode={isFreecode}
                downloadReport={(type, anonymiseReport, showDescription) =>
                  downloadPdfReport(type, anonymiseReport, showDescription)
                }
              />
              {report?.company?.departments?.length > 1 && ['super-admin', 'company-admin'].includes(userRole) && (
                <ChangeDepartmentDropdown
                  ticket={report.ticket}
                  departments={report.company.departments}
                  onDepartmentChange={(d) => {
                    setShowConfirmationModal(true);
                    setModalInfo({
                      title: 'Confirmation',
                      body: `Do you want to move the ticket to ${d.label} department?`,
                      department: d.value,
                    });
                  }}
                />
              )}
            </ButtonContainer>
            <InfoAndScoreContainer>
              <InfoAndScoreBox>
                <Heading>INFO</Heading>
                <HeadingUnderLine />
                {report.ticket && (
                  <InfoBox
                    report={report}
                    isFreecode={isFreecode}
                    totalMaxScore={totalMaxScore}
                    bestRunInput={bestRunInput}
                    getOvertimeUsed={getOvertimeUsed}
                    timeUsed={timeUsed}
                    completedAt={completedAt}
                    anonymiseReport={anonymiseReport}
                  />
                )}
              </InfoAndScoreBox>
              <InfoAndScoreBox>
                <Heading>{isFreecode && report?.ticket?.bestRun ? 'BEST SCORE' : 'SCORE'}</Heading>
                <HeadingUnderLine />
                {report.ticket && renderScoreBox()}
              </InfoAndScoreBox>
            </InfoAndScoreContainer>
            {report.ticket && (
              <TabSelector
                isReportPage={true}
                items={[
                  { label: 'STATISTICS', component: renderStatistics() },
                  { label: 'DESCRIPTION', component: renderDescriptionBox() },
                  { label: 'ANSWERS', component: renderAnswersBox() },
                ]}
                getActiveIndex={(index) => setActiveIndexTab(index)}
              ></TabSelector>
            )}
            {isFreecode && activeIndexTab === 0 && report?.ticket && renderDistribution(true)}
            {!isFreecode && activeIndexTab === 0 && report.test && report.answers.length > 0 && (
              <CategoryDistribution
                test={report.test}
                report={report}
                onInfoClick={() => {
                  setModalInformationKey('testCategories');
                  setShowScoreInstructionsModal(true);
                }}
              />
            )}
            {isFreecode && activeIndexTab === 2 && bestRunInput?._id && (
              <TestCases bestRunInput={bestRunInput} totalScore={totalScore} totalMaxScore={totalMaxScore} />
            )}
            {!isFreecode && activeIndexTab === 2 && report.answers.length > 0 && (
              <MCQAnswersTab report={report} isSuperAdmin={userRole === userRoles.SUPER_ADMIN} />
            )}
            {/* For pdf download */}
            {report.test && session && isCompleteReport && showDescription && renderDescriptionBox()}
            {report.test && session && isCompleteReport && renderAnswersBox()}
            {report.test && session && isCompleteReport && !isFreecode && (
              <MCQAnswersTab report={report} userRole={userRole} />
            )}
            {report.test && session && isFreecode && (
              <TestCases bestRunInput={bestRunInput} totalScore={totalScore} totalMaxScore={totalMaxScore} />
            )}
          </>
        )}
      </Container>
    </Wrapper>
  );
};

export default ReportPage;
